import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from 'components/base/Button';
import Rating from 'components/base/Rating';
import ProductColorNav from 'components/navs/ProductColorNav';
import { productColorVariants, productReviews } from 'data/e-commerce';
import {
  copyToClipboard,
  currencyFormat,
  getDeliveryDate,
  PF
} from 'helpers/utils';
import ProductGallery from 'components/modules/e-commerce/ProductGallery';
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import {
  Card,
  Pagination,
  Col,
  Row,
  Stack,
  InputGroup,
  FormControl
} from 'react-bootstrap';
import { Form, Link, useParams } from 'react-router-dom';
import QuantityButtons from 'components/common/QuantityButtons';
import { faHeart } from '@fortawesome/free-regular-svg-icons';
import {
  faBolt,
  faCalendarAlt,
  faChevronLeft,
  faChevronRight,
  faClock,
  faHeartCircleMinus,
  faMoneyBillWave,
  faShareAlt,
  faShoppingCart,
  faTicketAlt,
  faTruck
} from '@fortawesome/free-solid-svg-icons';
import {
  getAllProductReviewByProductId,
  getAllProducts,
  Product,
  ProductDetail,
  ProductReviewItem,
  ProductVariant,
  ReviewsData
} from 'api/products/_requests';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { RootState } from 'redux-state/store';
import toast from 'react-hot-toast';
import { addToWishlist, removeFromWishlist } from 'api/user/_requests';
import {
  addToWishlist as addToWishlistAction,
  removeFromWishlist as removeFromWishlistAction
} from 'redux-state/user/wishlistSlice';
import { addToCart } from 'api/cart/_requests';
import { addProductsToVendor as addToCartAction } from 'redux-state/user/cartSlice';
import { useCountdown } from 'hooks/useCountdown';
import CouponAndMore from 'components/common/CouponAndMore';
import ProductDetailLists from './ProductDetailLists';
import ProductBenefitsWarranty from './productDetails/ProductBenefitsWarrenty';
import SimilarProducts from 'components/sliders/SimilarProducts';
import { ProductCat } from 'data/e-commerce/products';
import RateProductDetailButton from 'components/cards/RateProductDetailButton';
import ProductReview from 'components/list-items/ProductReview';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import ProductReviewData from 'components/list-items/ProductReviewData';
import ProductFAQs from './productDetails/ProductFAQs';

export type VariantTypeSet = {
  color: string[];
  size: string[];
  weight: string[];
};

interface ALlProduct {
  id: string;
  image: string;
  name: string;
  rating: number;
  rated?: number;
  price?: number;
  salePrice?: number;
  colors?: number;
  extra?: string;
  extraClass?: string;
  extra2?: string;
  extra2Class?: string;
  verified?: boolean;
  wishListed?: boolean;
  offer?: string;
  dealEndTime?: string;
}

interface ProductDescriptionProps {
  product: ProductDetail;
  selectedVariant: ProductVariant;
  setSelectedVariant: Dispatch<SetStateAction<ProductVariant | undefined>>;
  variantTypes: VariantTypeSet;
  productId?: string;
}

const ProductDescription = ({
  product,
  selectedVariant,
  setSelectedVariant,
  variantTypes,
  productId
}: ProductDescriptionProps) => {
  const [selectedVariantKey, setSelectedVariantKey] = useState(
    selectedVariant?.color
  );
  const [selectedSizeKey, setSelectedSizeKey] = useState(
    selectedVariant?.size || variantTypes.size[0]
  );
  const [selectedWeightKey, setSelectedWeightKey] = useState(
    selectedVariant?.weight || variantTypes.weight[0]
  );
  const [quantity, setQuantity] = useState(1);
  const [couponApplied, setCouponApplied] = useState('');
  const dispatch = useDispatch();
  const { user } = useSelector((state: RootState) => state.auth);
  const { wishlist } = useSelector((state: RootState) => state?.user.wishlist);
  const [allProducts, setAllProducts] = useState<ProductCat[]>([]);
  const [pincode, setPincode] = useState('110092');
  const [page, setPage] = useState(1);
  const [quantityDiscount, setQuantityDiscount] = useState<number>(0);
  const [appliedPrice, setAppliedPrice] = useState<number>(
    () => selectedVariant.discountedPrice
  );

  const { data: reviewData, isFetching } = useQuery<ReviewsData>({
    queryKey: ['all-ProductReview-Product', productId, page], // depend on page
    queryFn: () => getAllProductReviewByProductId(productId ?? '', page, 3),
    placeholderData: keepPreviousData,
    enabled: !!productId
  });

  const getAvailableSizes = (color: string): string[] => {
    const sizes = product.variants
      .filter(v => v.color === color)
      .map(v => v.size)
      .filter((s): s is string => typeof s === 'string');
    return Array.from(new Set(sizes));
  };

  useEffect(() => {
    setAppliedPrice(selectedVariant.discountedPrice);
    const availableSizes = getAvailableSizes(selectedVariantKey);
    if (availableSizes.length > 0) {
      if (!availableSizes.includes(selectedSizeKey)) {
        setSelectedSizeKey(availableSizes[0]);
      }
    }
  }, [selectedVariantKey]);

  useEffect(() => {
    if (!selectedVariant.price) return;
    setQuantityDiscount(
      Math.round(
        ((selectedVariant.price - appliedPrice) * 100) / selectedVariant.price
      )
    );
  }, [selectedVariant.price, appliedPrice, quantity]);

  useEffect(() => {
    const fetchProducts = async () => {
      const products = await getAllProducts();
      const temp = products
        ?.filter((item: { _id: string }) => item._id !== productId)
        .map(
          (item: {
            _id: string;
            thumbnail: string | undefined;
            name: string;
            price: number;
            discountedPrice: number;
            vendorId: string;
          }) => ({
            id: item._id,
            image:
              PF + (item?.thumbnail === undefined ? 'goods' : item?.thumbnail),
            name: item.name,
            price: item.price,
            salePrice: item.discountedPrice,
            vendorId: item.vendorId
          })
        );
      setAllProducts(temp);
    };

    fetchProducts();
  }, []);

  useEffect(() => {
    const variant = product.variants.find(
      variant =>
        variant.color === selectedVariantKey &&
        variant.size === selectedSizeKey &&
        variant.weight === selectedWeightKey
    );
    if (variant) {
      setSelectedVariant(variant);
    }
  }, [selectedVariantKey, selectedSizeKey, selectedWeightKey]);

  const isWishlisted = useMemo(() => {
    return wishlist.some(
      item =>
        item.productId === product._id &&
        item.variantId === selectedVariant?._id
    );
  }, [wishlist, product._id, selectedVariant?._id]);

  const handleWishlistClick = async () => {
    if (!user) {
      toast.error('You must be logged in to add to wishlist');
      return;
    }
    if (user.role !== 'customer') {
      toast.error('You must be a customer to add to wishlist');
      return;
    }

    if (isWishlisted) {
      const data = {
        productId: product._id,
        variantId: selectedVariant?._id || null
      };
      await removeFromWishlist(data)
        .then(() => {
          dispatch(removeFromWishlistAction(data));
        })
        .catch(err => {
          toast.error('Failed to remove from wishlist');
        });
    } else {
      const data = {
        productId: product._id,
        variantId: selectedVariant?._id || null
      };
      await addToWishlist(data)
        .then(() => {
          dispatch(addToWishlistAction(data));
        })
        .catch(err => {
          toast.error('Failed to add to wishlist');
        });
    }
  };

  const handleAddToCart = async (product: ProductDetail) => {
    if (!user) {
      toast.error('You must be logged in to add to cart');
      return;
    }
    if (user.role !== 'customer') {
      toast.error('You must be a customer to add to cart');
      return;
    }

    const data = {
      vendorId: product.vendorId,
      product: {
        productId: product._id,
        variantId: selectedVariant?._id || '',
        price: selectedVariant?.discountedPrice,
        quantity: quantity
      }
    };
    await addToCart(data)
      .then(res => {
        dispatch(
          addToCartAction({
            vendorId: product.vendorId,
            products: {
              productId: product._id,
              price: selectedVariant?.discountedPrice,
              quantity: quantity,
              name: product.name,
              thumbnail: product.thumbnail,
              vendorId: product.vendorId,
              variantId: selectedVariant?._id || '',
              discountedPrice: selectedVariant?.discountedPrice,
              color: selectedVariant?.color || '',
              size: selectedVariant?.size || '',
              weight: selectedVariant?.weight || ''
            },
            totalAmount: selectedVariant?.discountedPrice,
            totalAmountAfterDiscount: 0
          })
        );
        toast.success('Added to cart');
      })
      .catch(err => {
        toast.error('Failed to add to cart');
      });
  };

  const pricingTiers = [
    { range: 'Qty 2', price: 620, min: 1, max: 2 },
    { range: 'Qty 3', price: 600, min: 3, max: 3 },
    { range: 'Qty 4 – 5', price: 582, min: 4, max: 5 },
    { range: 'Qty 6 – 7', price: 568, min: 6, max: 7 },
    { range: 'Qty 8+', price: 540, min: 8, max: Infinity }
  ];

  return (
    <Row className="gx-1">
      <Col xs={12} lg={9} className="align-self-start" style={{ top: 120 }}>
        <div className="card p-2 h-100">
          <Row>
            {selectedVariant?.images && (
              <Col xs={12} lg={5} className="position-relative mb-4 mb-lg-0">
                <div
                  style={{
                    position: 'sticky',
                    top: 5,
                    zIndex: 1,
                    borderRadius: '10px',
                    padding: '10px',
                    border: '1px solid #e6e6e6'
                  }}
                >
                  <ProductGallery images={selectedVariant.images} />
                </div>
              </Col>
            )}
            <Col xs={12} lg={7}>
              <div style={{ minHeight: '100vh' }}>
                <div className="mb-4">
                  <h3 className="mb-2 lh-sm fs-6 fw-bold">{product.name}</h3>

                  <div className="d-flex align-items-center mb-2">
                    <div className="me-2">
                      <Rating
                        readonly
                        initialValue={product.totalRanking || 0}
                        size={15}
                      />
                    </div>
                    <p className="text-muted fw-normal fs-8 mb-0">
                      ({product.totalReview || 0} Reviewed)
                    </p>
                  </div>

                  <div className="mb-2">
                    <p className="text-muted fs-8 mb-0">
                      {currencyFormat(selectedVariant?.price, {
                        currency: 'SAR'
                      })}
                      (Incl. of all taxes)
                    </p>

                    {}
                    <h3 className="fw-bold fs-5 text-dark mb-1">
                      {currencyFormat(appliedPrice, { currency: 'SAR' })}
                    </h3>

                    <div className="d-flex align-items-center gap-2">
                      <p className="text-decoration-line-through text-muted fs-7 mb-0">
                        {currencyFormat(selectedVariant?.price, {
                          currency: 'SAR'
                        })}
                      </p>
                      <p className="text-success fw-semibold fs-7 mb-0">
                        {quantityDiscount}% OFF
                      </p>
                    </div>
                  </div>

                  <div className="mb-2">
                    <span className="text-success fw-semibold fs-7">
                      {selectedVariant?.inStock ? 'In stock' : 'Out of stock'}
                    </span>
                  </div>
                </div>

                <div>
                  {variantTypes.color.length > 0 && (
                    <div className="mb-3">
                      <p className="fw-bold mb-1 text-body">Colour:</p>
                      <ProductColorNav
                        selectedVariantKey={selectedVariantKey}
                        setSelectedVariantKey={setSelectedVariantKey}
                        colorVariants={variantTypes.color}
                      />
                    </div>
                  )}

                  <div className="row g-3 g-sm-4 align-items-end">
                    {variantTypes.size.length > 0 && (
                      <div className="col-12 col-sm-auto">
                        <p className="fw-bold mb-1 text-body">Nominal Size:</p>
                        <div className="d-flex flex-wrap gap-2">
                          {variantTypes.size.map(size => (
                            <button
                              key={size}
                              type="button"
                              onClick={() => setSelectedSizeKey(size)}
                              className={`btn px-3 py-1 rounded fs-8 border-2 ${
                                selectedSizeKey === size
                                  ? 'border-danger text-danger'
                                  : 'border-dark text-dark'
                              }`}
                            >
                              {size}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {variantTypes.weight.length > 0 && (
                      <div className="col-12 col-sm-auto">
                        <p className="fw-bold mb-1 text-body">Weight:</p>
                        <div className="d-flex flex-wrap gap-2">
                          {variantTypes.weight.map(weight => (
                            <button
                              key={weight}
                              type="button"
                              onClick={() => setSelectedWeightKey(weight)}
                              className={`btn px-3 py-1 rounded fs-8 border-2 ${
                                selectedWeightKey === weight
                                  ? 'border-danger text-danger'
                                  : 'border-dark text-dark'
                              }`}
                            >
                              {weight}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="w-100 mx-0">
                      <CouponAndMore
                        productId={product._id}
                        couponApplied={couponApplied}
                        setCouponApplied={setCouponApplied}
                      />
                    </div>
                    <div>
                      <p className="fw-bold mb-2 text-body">
                        Buy More & Save More
                      </p>
                      <div className="d-flex flex-wrap gap-2 ms-1">
                        {product.bulkOffer.map((tier, i) => {
                          const active =
                            quantity >= (tier.price ?? 0) &&
                            quantity <= (tier.price || 0);

                          return (
                            <button
                              key={i}
                              type="button"
                              className={`btn text-center border rounded-3 shadow-sm px-2 py-2 transition-all ${
                                active
                                  ? 'border-danger bg-warning bg-opacity-10 text-dark'
                                  : 'border-light bg-white text-dark'
                              }`}
                              style={{
                                minWidth: 140,
                                transition: 'all 0.2s ease',
                                boxShadow: active
                                  ? '0 0 0 2px rgba(220, 53, 69, 0.25)'
                                  : '0 1px 3px rgba(0, 0, 0, 0.1)'
                              }}
                              onClick={() => {
                                setQuantity(tier.quantity ?? 0);
                                setAppliedPrice(
                                  tier.price ?? selectedVariant.discountedPrice
                                );
                              }}
                              onMouseEnter={e =>
                                (e.currentTarget.style.transform =
                                  'scale(1.03)')
                              }
                              onMouseLeave={e =>
                                (e.currentTarget.style.transform = 'scale(1)')
                              }
                            >
                              <p className="fw-semibold mb-1 fs-8">
                                Qty {tier.quantity}
                              </p>
                              <p className="mb-1 fs-8">
                                SAR {tier.price}
                                <small className="text-muted"> /pc</small>
                              </p>
                              <p className="text-success fw-bold mb-0 small">
                                {(
                                  ((selectedVariant.price - (tier.price ?? 0)) *
                                    100) /
                                  selectedVariant.price
                                ).toFixed(1)}
                                % OFF
                              </p>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    <div className="">
                      <ProductDetailLists
                        keyFeature={product.keyfeature}
                        specification={product.specification || []}
                        inBox={product.inBox}
                        description={product.description}
                      />
                    </div>
                    <div className="">
                      <ProductBenefitsWarranty />
                    </div>
                    <div className="col-12 col-sm">
                      <Button
                        variant="phoenix-primary"
                        className="px-3 border-0 mt-2"
                        onClick={() => {
                          copyToClipboard(
                            `${process.env.REACT_APP_FRONTEND_URL}/product-details/${product._id}`
                          );
                          toast.success('Link copied to clipboard');
                        }}
                      >
                        <FontAwesomeIcon icon={faShareAlt} className="fs-7" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
          <Row>
            <Col xs={12} className="px-3 mt-3 mb-3">
              <Card>
                <Card.Body className="p-3">
                  <SimilarProducts
                    subCategoryId={product.subCategory ?? ''}
                    productId={product._id}
                  />
                </Card.Body>
              </Card>
            </Col>
          </Row>
          <Row>
            <div className="mb-3 ">
              <Card>
                <Card.Header className="pb-0 border-bottom-0">
                  <Stack
                    gap={3}
                    direction="horizontal"
                    className="flex-wrap justify-content-between"
                  >
                    <div className="d-flex align-items-center flex-wrap">
                      <h2 className="fw-bolder me-3">
                        {product.totalRanking}
                        <span className="fs-8 text-body-quaternary fw-bold">
                          /5
                        </span>
                      </h2>

                      <div className="me-3">
                        <Rating
                          initialValue={product.totalRanking}
                          readonly
                          iconClass="fs-6"
                        />
                      </div>

                      <p className="text-body mb-0 fw-semibold fs-7">
                        {product.totalRating} ratings and {product.totalReview}{' '}
                        reviews
                      </p>
                    </div>
                    <RateProductDetailButton product={product} />
                  </Stack>
                </Card.Header>
                <Card.Body>
                  {reviewData?.reviews?.map(r => (
                    <ProductReviewData key={r._id} review={r} />
                  ))}
                  <Pagination className="mb-0 justify-content-center">
                    <Pagination.Prev
                      disabled={page === 1}
                      onClick={() => setPage(p => Math.max(p - 1, 1))}
                    >
                      <FontAwesomeIcon icon={faChevronLeft} />
                    </Pagination.Prev>

                    {[...Array(reviewData?.totalPages ?? 0)].map((_, i) => {
                      const p = i + 1;
                      return (
                        <Pagination.Item
                          key={p}
                          active={p === page}
                          onClick={() => setPage(p)}
                        >
                          {p}
                        </Pagination.Item>
                      );
                    })}

                    <Pagination.Next
                      disabled={page === reviewData?.totalPages}
                      onClick={() =>
                        setPage(p =>
                          reviewData?.totalPages
                            ? Math.min(p + 1, reviewData.totalPages)
                            : p
                        )
                      }
                    >
                      <FontAwesomeIcon icon={faChevronRight} />
                    </Pagination.Next>
                  </Pagination>
                </Card.Body>
              </Card>
            </div>
          </Row>
          <Row>
            <Col xs={12} className="px-3 mt-3 mb-3">
              <ProductFAQs
                productId={productId ?? ''}
                vendorId={product.vendorId ?? ''}
                productVariantId={product.productVariantId}
              />
            </Col>
          </Row>
        </div>
      </Col>
      <Col
        xs={12}
        lg={3}
        className="sticky-top align-self-start"
        style={{ top: 120 }}
      >
        <div className="card p-4 shadow-sm">
          <div className="mb-4">
            <p className="text-muted fs-8 mb-0">
              {currencyFormat(selectedVariant?.price, { currency: 'SAR' })}{' '}
              (Incl. of all taxes)
            </p>

            <h3 className="fw-bold fs-5 text-dark mb-1">
              {currencyFormat(appliedPrice, {
                currency: 'SAR'
              })}
            </h3>

            <div className="d-flex align-items-center gap-2">
              <p className="text-decoration-line-through text-muted fs-7 mb-0">
                {currencyFormat(selectedVariant?.price, { currency: 'SAR' })}
              </p>
              <p className="text-success fw-semibold fs-7 mb-0">
                {quantityDiscount}% OFF
              </p>
            </div>
          </div>
          {user?.role === 'customer' && (
            <div className="mb-4">
              <div className="d-flex justify-content-between mb-2">
                <p className="fw-semibold mb-0">Quantity</p>
                <QuantityButtons
                  quantity={quantity}
                  setQuantity={setQuantity}
                  productId={product._id}
                  vendorId={product.vendorId}
                  variantId={selectedVariant?._id || ''}
                  formCart={false}
                />
              </div>
            </div>
          )}
          <div className="d-grid gap-2 mb-2">
            <Button
              variant={isWishlisted ? 'outline-danger' : 'outline-primary'}
              size="lg"
              className="fw-semibold py-3 mb-2"
              onClick={handleWishlistClick}
            >
              <FontAwesomeIcon
                icon={isWishlisted ? faHeartCircleMinus : faHeart}
                className="me-2"
              />
              {isWishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
            </Button>

            <Button
              variant="danger"
              size="lg"
              className="fw-semibold py-3 text-white"
              onClick={() => handleAddToCart(product)}
            >
              <FontAwesomeIcon icon={faShoppingCart} className="me-2" />
              Add to cart
            </Button>
          </div>
        </div>
        <Card
          className="p-3 shadow-sm mt-3 w-100"
          style={{ fontSize: '0.9rem' }}
        >
          <h6 className="mb-3">Delivery Details</h6>

          <InputGroup size="sm" className="mb-2">
            <FormControl /* ✅ use FormControl */
              type="text"
              value={pincode}
              placeholder="Enter Pincode"
              onChange={
                (e: React.ChangeEvent<HTMLInputElement>) =>
                  setPincode(e.currentTarget.value) /* ✅ typed event */
              }
            />
            <Button variant="danger">CHECK</Button>
          </InputGroup>

          <div className="text-muted mb-3" style={{ fontSize: '0.75rem' }}>
            Check serviceability at your location
          </div>

          <div
            className="p-2 rounded text-white mb-3"
            style={{ backgroundColor: '#222' }}
          >
            <div className="d-flex align-items-center mb-1">
              <FontAwesomeIcon icon={faBolt} className="me-2 text-warning" />
              <strong>Get it in 24 hours</strong>
            </div>
            <div style={{ fontSize: '0.75rem' }}>
              (Today or Tomorrow)
              <br />
              <span className="text-warning">
                Only Online payment applicable
              </span>
            </div>
          </div>

          <div className="d-flex align-items-start mb-2">
            <FontAwesomeIcon
              icon={faTruck}
              className="text-primary me-2 mt-1"
            />
            <div>
              <strong className="text-success">Free Delivery</strong>
              <br />
              <small>No shipping charge on this order</small>
            </div>
          </div>

          <div className="d-flex align-items-start mb-2">
            <FontAwesomeIcon
              icon={faClock}
              className="text-success me-2 mt-1"
            />
            <div>
              <strong>Regular delivery</strong> also available at {pincode} in 2
              day(s)
            </div>
          </div>

          <div className="d-flex align-items-start">
            <FontAwesomeIcon
              icon={faMoneyBillWave}
              className="text-success me-2 mt-1"
            />
            <div>
              <strong className="text-success">COD Available</strong>
              <br />
              <small>You can pay at the time of delivery</small>
            </div>
          </div>
        </Card>
      </Col>
    </Row>
  );
};

export default ProductDescription;
